import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { ClipboardList, Search, Home, ArrowRight, Check, Users, Building2, ShieldCheck } from 'lucide-react';

const HERO_BG = 'https://images.unsplash.com/photo-1771504184695-c2dfceb8e08f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHw0fHxnZW5ldmElMjBza3lsaW5lJTIwbGFrZSUyMGFyY2hpdGVjdHVyYWx8ZW58MHx8fHwxNzc2MzM4NjM5fDA&ixlib=rb-4.1.0&q=85';
const INTERIOR_IMG = 'https://static.prod-images.emergentagent.com/jobs/28fc0be7-4196-4b45-b5b2-7034b2e56bae/images/aa91d9027ad5f5398a27222b5517df82012780b22f70d2168b6b643e8a585f04.png';
const BUILDING_IMG = 'https://static.prod-images.emergentagent.com/jobs/28fc0be7-4196-4b45-b5b2-7034b2e56bae/images/4da1f999244d1d62b76c88098b0dc0b7bb7ea1c2b1ed681f377e3a18eb230b69.png';
const LOGO_URL = 'https://static.prod-images.emergentagent.com/jobs/28fc0be7-4196-4b45-b5b2-7034b2e56bae/images/d88baf08cf9aab8cd54854b5018978ec360b4e238c0fbb7dabe14674d502f36a.png';

export default function LandingPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleCTA = () => {
    navigate('/application');
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B3D2E]/90 via-[#0B3D2E]/70 to-[#0B3D2E]/30" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <div className="max-w-2xl animate-fade-in-up">
            <p className="text-xs tracking-[0.25em] uppercase text-white/60 mb-6 font-semibold">Premium Relocation Service</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight font-light leading-tight">
              {t('hero.title')}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/75 leading-relaxed max-w-xl">
              {t('hero.subtitle')}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button
                onClick={handleCTA}
                className="bg-white text-[#0B3D2E] hover:bg-white/90 px-8 py-3 h-auto text-base font-medium rounded-md shadow-lg hover:shadow-xl transition-all"
                data-testid="hero-cta-btn"
              >
                {t('hero.cta')} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-white/30 text-white hover:bg-white/10 px-8 py-3 h-auto text-base rounded-md"
                data-testid="hero-secondary-btn"
              >
                {t('hero.secondary')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 lg:py-32 px-6 lg:px-12 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] uppercase text-[#0B3D2E] font-semibold mb-3">The Process</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-[#2B2B2B] tracking-tight">{t('how.title')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
            {[
              { icon: ClipboardList, title: t('how.s1.title'), desc: t('how.s1.desc'), step: '01' },
              { icon: ShieldCheck, title: t('how.s2.title'), desc: t('how.s2.desc'), step: '02' },
              { icon: Search, title: t('how.s3.title'), desc: t('how.s3.desc'), step: '03' },
              { icon: Home, title: t('how.s4.title'), desc: t('how.s4.desc'), step: '04' },
            ].map((item, i) => (
              <div key={i} className="relative p-8 lg:p-10 bg-white border border-black/5 rounded-lg hover-lift group" data-testid={`how-step-${i+1}`}>
                <span className="text-5xl font-heading font-light text-[#0B3D2E]/10 absolute top-4 right-6">{item.step}</span>
                <div className="w-12 h-12 rounded-md bg-[#0B3D2E]/5 flex items-center justify-center mb-6 group-hover:bg-[#0B3D2E]/10 transition-colors">
                  <item.icon className="w-5 h-5 text-[#0B3D2E]" />
                </div>
                <h3 className="font-heading text-xl font-medium text-[#2B2B2B] mb-3">{item.title}</h3>
                <p className="text-sm text-[#5a5a5a] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 rounded-lg border border-[#0B3D2E]/15 bg-[#0B3D2E]/5 p-6" data-testid="how-validation-note">
            <h3 className="font-heading text-xl text-[#2B2B2B]">{t('how.shiftTitle')}</h3>
            <p className="text-sm text-[#2B2B2B] mt-2">{t('how.shiftDesc')}</p>
            <ul className="mt-4 space-y-2">
              {['how.shiftBullet1', 'how.shiftBullet2', 'how.shiftBullet3'].map((key) => (
                <li key={key} className="flex items-start gap-2 text-sm text-[#2B2B2B]">
                  <Check className="w-4 h-4 mt-0.5 text-[#0B3D2E] shrink-0" />
                  <span>{t(key)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 lg:py-32 px-6 lg:px-12 section-beige" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] uppercase text-[#0B3D2E] font-semibold mb-3">Advantages</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-[#2B2B2B] tracking-tight">{t('ben.title')}</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
            {/* Individuals */}
            <div className="bg-white p-8 lg:p-10 rounded-lg border border-black/5 shadow-sm" data-testid="benefits-individuals">
              <div className="flex items-center gap-3 mb-6">
                <Users className="w-5 h-5 text-[#0B3D2E]" />
                <h3 className="font-heading text-xl font-medium text-[#2B2B2B]">{t('ben.ind')}</h3>
              </div>
              <ul className="space-y-3">
                {['ben.b1', 'ben.b2', 'ben.b3', 'ben.b4'].map(k => (
                  <li key={k} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[#0B3D2E] mt-0.5 shrink-0" />
                    <span className="text-sm text-[#2B2B2B]">{t(k)}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* Companies */}
            <div className="bg-white p-8 lg:p-10 rounded-lg border border-black/5 shadow-sm" data-testid="benefits-companies">
              <div className="flex items-center gap-3 mb-6">
                <Building2 className="w-5 h-5 text-[#0B3D2E]" />
                <h3 className="font-heading text-xl font-medium text-[#2B2B2B]">{t('ben.corp')}</h3>
              </div>
              <ul className="space-y-3">
                {['ben.b5', 'ben.b6', 'ben.b7', 'ben.b8'].map(k => (
                  <li key={k} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[#0B3D2E] mt-0.5 shrink-0" />
                    <span className="text-sm text-[#2B2B2B]">{t(k)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Interior showcase */}
      <section className="relative py-20 lg:py-32 overflow-hidden" data-testid="showcase-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-[#0B3D2E] font-semibold mb-3">Premium Access</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-[#2B2B2B] tracking-tight mb-6">
              Exclusive Listings,<br />Professionally Managed
            </h2>
            <p className="text-[#5a5a5a] leading-relaxed mb-8 max-w-lg">
              Our network gives you access to apartments before they hit the open market. Combined with our professional dossier preparation, your chances of securing housing increase significantly.
            </p>
            <Button onClick={handleCTA} className="bg-[#0B3D2E] hover:bg-[#0f523e] text-white px-8 py-3 h-auto" data-testid="showcase-cta-btn">
              {t('cta.button')} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <div className="relative">
            <img src={INTERIOR_IMG} alt="Premium apartment" className="rounded-lg shadow-xl max-h-[80vh] md:max-h-none w-full object-cover" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-20 lg:py-32" data-testid="final-cta-section">
        <div className="absolute inset-0">
          <img src={BUILDING_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0B3D2E]/85" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight mb-4">{t('cta.title')}</h2>
          <p className="text-white/70 mb-10 max-w-xl mx-auto">{t('cta.subtitle')}</p>
          <Button onClick={handleCTA} className="bg-white text-[#0B3D2E] hover:bg-white/90 px-10 py-3 h-auto text-base font-medium" data-testid="final-cta-btn">
            {t('cta.button')} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 bg-[#2B2B2B]" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mb-6">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Nest Geneva" className="h-7 w-auto invert" />
              <span className="font-heading text-base text-white/80">Nest Geneva</span>
            </div>
            <nav className="flex items-center gap-6">
              <a href="/#how-it-works" className="text-sm text-white/60 hover:text-white transition-colors" data-testid="footer-how-link">{t('nav.howItWorks')}</a>
              <a href="/services" className="text-sm text-white/60 hover:text-white transition-colors" data-testid="footer-services-link">{t('svc.label')}</a>
            </nav>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-white/40 text-xs">&copy; {new Date().getFullYear()} Nest Geneva. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <p className="text-white/30 text-xs">Geneva, Switzerland</p>
              <button
                type="button"
                onClick={() => navigate('/admin')}
                className="text-[10px] text-white/25 hover:text-white/45 transition-colors"
                data-testid="footer-admin-btn"
              >
                Admin access
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
